import React, { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import PencilToSquare from '@gravity-ui/icons/PencilToSquare';
import { Button, Icon, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { updateAliceAgentApp } from 'client/api/services/aliceAgentApps';
import editStyles from 'client/blocks/AliceAgentAppPage/AliceAgentAppEditPage.module.scss';
import { SettingPageLayout } from 'client/blocks/Layout/Layout';
import { SideMenu } from 'client/blocks/Menu/Menu';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceExternalAppForm';

import styles from './AliceExternalAppEditPage.module.scss';

const t = i18n(keyset);

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    onSaved: () => Promise<unknown> | void;
    children: ReactNode;
}

/**
 * Shared layout for every External-agent tab: sidebar (logo + version switcher + side menu) on
 * the left, big content header + status banners + tab body on the right. Mirrors the legacy
 * `AliceSkillAppPage` structure so that navigating between Settings and Account Linking keeps
 * the surrounding chrome stable.
 *
 * Tab body is passed in via `children` — the container renders `<Routes>` inside this layout
 * so React Router swaps only the inner content.
 */
export const AliceExternalAppLayout: FC<Props> = ({ app, isDraft, onSaved, children }) => {
    const navigate = useNavigate();
    const { add: toasterAdd } = useToaster();

    const displayName = app.draft.name || t('agent-fallback-title');
    const logoUrl = app.draft.logo?.url;
    const hasPublished = Boolean(app.published);
    const status = app.draft.status;
    const isProcessing = status === 'mcpProcessing';
    const isFailed = status === 'mcpFailed';

    const navigateToVersion = (draft: boolean) => navigate(routes.app.aliceExternalApp(app.skillId, draft));

    // hash-anchors target the *currently viewed* settings page (draft or published), while the
    // Authorization tab only exists for the draft — published has no editable account-linking.
    const settingsPath = routes.app.aliceExternalApp(app.skillId, isDraft);
    const authorizationPath = routes.app.aliceExternalApp(app.skillId, true, 'authorization');

    const menuItems = [
        { value: `${settingsPath}#basic`, content: t('side-menu-basic') },
        { value: `${settingsPath}#mcp`, content: t('side-menu-mcp-server') },
        { value: `${settingsPath}#tools-resources`, content: t('side-menu-tools-resources') },
        { value: authorizationPath, content: t('side-menu-account-linking') },
    ];

    const handleRetryProcessing = async () => {
        // PATCH the saved manifest as-is. The backend treats any resource set as "changed" when
        // the previous status was `mcpFailed`, flipping the draft back to `mcpProcessing`.
        // Using `app.draft.manifest` (not the current form state) means retry doesn't need a
        // reference to the EditPage form — works the same from the Authorization tab too.
        if (!app.draft.manifest) return;
        try {
            await updateAliceAgentApp(app.skillId, { manifest: app.draft.manifest });
            await onSaved();
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-save-error');
            toasterAdd({ title: message, theme: 'danger', name: 'RETRY_MCP_PROCESSING' });
        }
    };

    return (
        <SettingPageLayout>
            <section className={editStyles.pageContainer}>
                <div className={editStyles.leftColumn}>
                    <div>
                        <div className={editStyles.menuHeader}>
                            <div className={editStyles.menuHeaderNameBlock}>
                                {logoUrl ? (
                                    <div
                                        className={editStyles.headerLogoSmall}
                                        style={{ backgroundImage: `url(${logoUrl})` }}
                                    />
                                ) : (
                                    <div className={editStyles.logoPlaceholderSmall}>
                                        {displayName.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <h4 className={editStyles.menuHeaderTitle}>{displayName}</h4>
                            </div>
                        </div>
                        <div className={editStyles.deploymentStatus}>
                            {hasPublished && (
                                <Button
                                    className={clx(
                                        editStyles.deploymentStatusButton,
                                        !isDraft && editStyles.deploymentStatusButton_active,
                                    )}
                                    onClick={() => navigateToVersion(false)}
                                    view="flat"
                                >
                                    <span className={editStyles.deploymentStatusRow}>
                                        <Icon
                                            className={editStyles.deploymentStatusIconPublished}
                                            data={CircleCheckFill}
                                        />
                                        {t('version-published')}
                                    </span>
                                </Button>
                            )}
                            <Button
                                className={clx(
                                    editStyles.deploymentStatusButton,
                                    isDraft && editStyles.deploymentStatusButton_active,
                                )}
                                onClick={() => navigateToVersion(true)}
                                view="flat"
                            >
                                <span className={editStyles.deploymentStatusRow}>
                                    <Icon data={PencilToSquare} />
                                    {t('version-draft')}
                                </span>
                            </Button>
                        </div>
                        <SideMenu menuItems={menuItems} />
                    </div>
                </div>
                <div className={editStyles.pageContent}>
                    <header className={editStyles.contentHeader}>
                        <div className={editStyles.contentHeaderInner}>
                            <div className={editStyles.appTitle}>
                                {logoUrl ? (
                                    <div
                                        className={editStyles.headerLogoMedium}
                                        style={{ backgroundImage: `url(${logoUrl})` }}
                                    />
                                ) : (
                                    <div className={editStyles.logoPlaceholderMedium}>
                                        {displayName.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <h1 className={editStyles.appTitleHeader}>{displayName}</h1>
                            </div>
                        </div>
                    </header>

                    {isProcessing && (
                        <div className={`${styles.banner} ${styles.bannerWarning}`}>
                            <strong className={styles.bannerTitle}>{t('processing-banner-title')}</strong>
                            <p className={styles.bannerBody}>{t('processing-banner-body')}</p>
                        </div>
                    )}
                    {isFailed && (
                        <div className={`${styles.banner} ${styles.bannerDanger}`}>
                            <strong className={styles.bannerTitle}>{t('failed-banner-title')}</strong>
                            <p className={styles.bannerBody}>
                                {app.draft.mcpProcessing?.lastError
                                    ? t('failed-banner-body-with-error', {
                                          error: app.draft.mcpProcessing.lastError,
                                      })
                                    : t('failed-banner-body')}
                            </p>
                            <Button view="action" onClick={handleRetryProcessing} className={styles.bannerActionRow}>
                                {t('action-retry-processing')}
                            </Button>
                        </div>
                    )}

                    {children}
                </div>
            </section>
        </SettingPageLayout>
    );
};
